/**
 * Sub-hook for PO Lines bulk selection and bulk operations (CR-3408).
 */
import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { purchasingAPI, PurchaseOrderLine } from '../../../../lib/api';
import { useAuth } from '../../../../contexts/AuthContext';
import { useUserPermissions } from '../../../../contexts/UserPermissionsContext';

interface BulkResultEntry {
  lineId: number;
  poNumber: string;
  lineNumber: number;
  error: string;
}

interface UsePOLinesBulkActionsParams {
  lines: PurchaseOrderLine[];
  fetchLines: () => Promise<void>;
  bustCacheRef: React.MutableRefObject<boolean>;
}

export function usePOLinesBulkActions({ lines, fetchLines, bustCacheRef }: UsePOLinesBulkActionsParams) {
  const { user } = useAuth();
  const { hasPurchasingWriteAccess } = useUserPermissions();

  // Bulk selection state
  const [selectedLineIds, setSelectedLineIds] = useState<Set<number>>(new Set());
  const [bulkAccepting, setBulkAccepting] = useState(false);
  const [bulkRejecting, setBulkRejecting] = useState(false);
  const [showBulkAcceptModal, setShowBulkAcceptModal] = useState(false);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [showBulkHoldModal, setShowBulkHoldModal] = useState(false);
  const [bulkHolding, setBulkHolding] = useState(false);

  // Bulk operation result log
  const [bulkResultLog, setBulkResultLog] = useState<BulkResultEntry[]>([]);
  const [showBulkResultLog, setShowBulkResultLog] = useState(false);

  // Only allow selecting lines where the action is waiting on the current user's role.
  // CR-4203: Also checks role-based site permissions for bulk operations
  const canSelectForBulkAccept = useCallback((line: PurchaseOrderLine): boolean => {
    if (!user || !user.platform) return false;
    // CR-4687 rework: Exclude closed/received lines from bulk selection — they
    // cannot be accepted/rejected and should not be selectable.
    // Note: "Closed" in UI = status 'received' in the API (fully delivered lines).
    if (line.status === 'cancelled' || line.status === 'received' || line.on_hold) return false;

    const isSupplier = user.platform === 'SUPPLIER' || user.platform === 'HELIX';
    const isCustomer = user.platform === 'CUSTOMER';

    // CR-4647: Site-based write access only applies to supplier users.
    // Customers don't have permissions on supplier sites, so the check would
    // always return false and hide bulk actions from them.
    if (isSupplier && !hasPurchasingWriteAccess(line.supplier_id)) return false;

    if (isSupplier && line.waiting_on === 'Supplier') return true;
    if (isCustomer && line.waiting_on === 'Customer') return true;

    return false;
  }, [user, hasPurchasingWriteAccess]);

  const getSelectableLines = useCallback(() => {
    return lines.filter(line => canSelectForBulkAccept(line));
  }, [lines, canSelectForBulkAccept]);

  // CR-4687 round 3: Sarah reported that the selection count drifted out of
  // sync with reality after per-line actions — UI said "3 selected" but only
  // remaining lines were actually selected because a status transition (e.g.
  // New → WIP) removed the actioned line from the selectable set. Prune the
  // selection set whenever ``lines`` refetches so the count always reflects
  // currently-selectable rows, regardless of which action triggered the
  // refetch (per-line modal, bulk action, filter change, page change).
  useEffect(() => {
    setSelectedLineIds(prev => {
      if (prev.size === 0) return prev;
      const selectableIds = new Set(
        lines.filter(line => canSelectForBulkAccept(line)).map(line => line.id)
      );
      const pruned = new Set(Array.from(prev).filter(id => selectableIds.has(id)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [lines, canSelectForBulkAccept]);

  const handleSelectAll = useCallback(() => {
    const selectable = getSelectableLines();
    if (selectedLineIds.size === selectable.length && selectable.length > 0) {
      setSelectedLineIds(new Set());
    } else {
      setSelectedLineIds(new Set(selectable.map(l => l.id)));
    }
  }, [getSelectableLines, selectedLineIds.size]);

  const handleToggleSelect = useCallback((lineId: number) => {
    setSelectedLineIds(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(lineId)) {
        newSelected.delete(lineId);
      } else {
        newSelected.add(lineId);
      }
      return newSelected;
    });
  }, []);

  const handleBulkAccept = useCallback(async () => {
    if (selectedLineIds.size === 0) return;

    setBulkAccepting(true);
    let successCount = 0;
    const failedLines: BulkResultEntry[] = [];

    const lineMap = new Map(lines.map(l => [l.id, l]));

    for (const lineId of Array.from(selectedLineIds)) {
      try {
        await purchasingAPI.acceptDeliveryDate(lineId, {});
        successCount++;
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { message?: string } } };
        const errorMsg = axiosErr?.response?.data?.message || (err instanceof Error ? err.message : 'Unknown error');
        const lineInfo = lineMap.get(lineId);
        failedLines.push({
          lineId,
          poNumber: lineInfo?.po_number || String(lineId),
          lineNumber: lineInfo?.line_number || 0,
          error: errorMsg,
        });
      }
    }

    setBulkAccepting(false);

    if (successCount > 0) {
      toast.success(`Accepted ${successCount} line${successCount > 1 ? 's' : ''} successfully`);
    }
    if (failedLines.length > 0) {
      setSelectedLineIds(new Set(failedLines.map(f => f.lineId)));
      setBulkResultLog(failedLines);
      setShowBulkResultLog(true);
      toast.error(`Failed to accept ${failedLines.length} line${failedLines.length > 1 ? 's' : ''} — see details below`);
    } else {
      setSelectedLineIds(new Set());
    }

    bustCacheRef.current = true;
    fetchLines();
  }, [selectedLineIds, lines, fetchLines, bustCacheRef]);

  const handleBulkReject = useCallback(async (reason: string) => {
    if (selectedLineIds.size === 0 || !reason) return;

    setBulkRejecting(true);
    let successCount = 0;
    const failedLines: BulkResultEntry[] = [];

    const lineMap = new Map(lines.map(l => [l.id, l]));

    for (const lineId of Array.from(selectedLineIds)) {
      try {
        await purchasingAPI.declineDeliveryDate(lineId, { reason });
        successCount++;
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { message?: string } } };
        const errorMsg = axiosErr?.response?.data?.message || (err instanceof Error ? err.message : 'Unknown error');
        const lineInfo = lineMap.get(lineId);
        failedLines.push({
          lineId,
          poNumber: lineInfo?.po_number || String(lineId),
          lineNumber: lineInfo?.line_number || 0,
          error: errorMsg,
        });
      }
    }

    setBulkRejecting(false);
    setShowBulkRejectModal(false);

    if (successCount > 0) {
      toast.success(`Rejected ${successCount} line${successCount > 1 ? 's' : ''} successfully`);
    }
    if (failedLines.length > 0) {
      setSelectedLineIds(new Set(failedLines.map(f => f.lineId)));
      setBulkResultLog(failedLines);
      setShowBulkResultLog(true);
      toast.error(`Failed to reject ${failedLines.length} line${failedLines.length > 1 ? 's' : ''} — see details below`);
    } else {
      setSelectedLineIds(new Set());
    }

    bustCacheRef.current = true;
    fetchLines();
  }, [selectedLineIds, lines, fetchLines, bustCacheRef]);

  const handleBulkPutOnHold = useCallback(async (reason: string) => {
    if (selectedLineIds.size === 0 || !reason) return;

    setBulkHolding(true);
    let successCount = 0;
    let failCount = 0;

    for (const lineId of Array.from(selectedLineIds)) {
      try {
        await purchasingAPI.putLineOnHold(lineId, { reason });
        successCount++;
      } catch (err) {
        failCount++;
        console.error(`Failed to put line ${lineId} on hold:`, err);
      }
    }

    setBulkHolding(false);
    setSelectedLineIds(new Set());
    setShowBulkHoldModal(false);

    if (successCount > 0) {
      toast.success(`Put ${successCount} line${successCount > 1 ? 's' : ''} on hold`);
    }
    if (failCount > 0) {
      toast.error(`Failed to put ${failCount} line${failCount > 1 ? 's' : ''} on hold`);
    }

    bustCacheRef.current = true;
    fetchLines();
  }, [selectedLineIds, fetchLines, bustCacheRef]);

  return {
    selectedLineIds,
    setSelectedLineIds,
    bulkAccepting,
    bulkRejecting,
    bulkHolding,
    showBulkAcceptModal,
    setShowBulkAcceptModal,
    showBulkRejectModal,
    setShowBulkRejectModal,
    showBulkHoldModal,
    setShowBulkHoldModal,
    bulkResultLog,
    showBulkResultLog,
    setShowBulkResultLog,
    canSelectForBulkAccept,
    getSelectableLines,
    handleSelectAll,
    handleToggleSelect,
    handleBulkAccept,
    handleBulkReject,
    handleBulkPutOnHold,
  };
}
